import React, { useState, useRef } from 'react';

const trackEvent = (eventName, params = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, { app: 'find_my_lead_magnet', ...params });
  }
};

function QuizForm({ onComplete }) {
  const hasStarted = useRef(false);
  const [formData, setFormData] = useState({
    businessCategory: '',
    pricePoint: '',
    audienceTemperature: '',
    customerTime: '',
    buyingHurdle: '',
    microWin: '',
    secretWeapon: '',
    promotionChannel: '',
    brandVibe: '',
    rawMaterial: ''
  });

  const handleChange = (e) => {
    if (!hasStarted.current) {
      hasStarted.current = true;
      trackEvent('quiz_started');
    }
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackEvent('quiz_completed');
    onComplete(formData);
  };

  const isFormValid = () => {
    return Object.values(formData).every(value => value.trim() !== '');
  };

  return (
    <form onSubmit={handleSubmit} className="quiz-form">

      <div className="question-group">
        <label>1. What is your primary business category?</label>
        <select name="businessCategory" value={formData.businessCategory} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="service-based">Service-Based (Coaching, Consulting, Freelance)</option>
          <option value="digital-products">Digital Products (Courses, Templates, Ebooks)</option>
          <option value="physical-products">Physical Products (Ecommerce, Local Shop)</option>
          <option value="professional-services">Professional Services (Real Estate, Legal, Finance)</option>
        </select>
      </div>

      <div className="question-group">
        <label>2. What is the price point of the main offer you want to sell?</label>
        <select name="pricePoint" value={formData.pricePoint} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="low-ticket">Low Ticket (Under $100)</option>
          <option value="mid-ticket">Mid Ticket ($100 to $1,000)</option>
          <option value="high-ticket">High Ticket ($1,000+)</option>
        </select>
      </div>

      <div className="question-group">
        <label>3. What is the "Temperature" of the audience you are targeting?</label>
        <select name="audienceTemperature" value={formData.audienceTemperature} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="cold">Cold: They don't know me yet (Need a quick win).</option>
          <option value="warm">Warm: They know me but haven't bought (Need trust/proof).</option>
          <option value="hot">Hot: They are ready to buy but need a final push (Need a sales-closer).</option>
        </select>
      </div>

      <div className="question-group">
        <label>4. How much time will your ideal customer give you for a "freebie"?</label>
        <select name="customerTime" value={formData.customerTime} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="2-minute">The 2-Minute Sprint: They want an instant answer or "hack."</option>
          <option value="10-minute">The 10-Minute Coffee Break: They'll read a short guide or watch a video.</option>
          <option value="deep-dive">The Deep Dive: They are in "student mode" and want to learn.</option>
        </select>
      </div>

      <div className="question-group">
        <label>5. What is the biggest hurdle stopping your ICP from buying?</label>
        <select name="buyingHurdle" value={formData.buyingHurdle} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="information-overload">Information Overload: They don't know where to start.</option>
          <option value="lack-of-tools">Lack of Tools: They know what to do but don't have the "stuff" to do it.</option>
          <option value="skepticism">Skepticism: They aren't sure if your specific method works.</option>
          <option value="complexity">Complexity: Your solution feels "too big" or intimidating.</option>
        </select>
      </div>

      <div className="question-group">
        <label>6. Which "Micro-Win" would help your customer most right now?</label>
        <select name="microWin" value={formData.microWin} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="roadmap">A roadmap or step-by-step path.</option>
          <option value="tools-list">A list of "Must-Have" tools or resources.</option>
          <option value="done-for-you">A "Done-For-You" template, script, or email.</option>
          <option value="self-assessment">A way to assess themselves (Quiz/Audit).</option>
        </select>
      </div>

      <div className="question-group">
        <label>7. What is your "Secret Weapon" as a business owner?</label>
        <select name="secretWeapon" value={formData.secretWeapon} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="strategy">Strategy: I'm great at breaking things into logical steps.</option>
          <option value="shortcuts">Shortcuts: I have the templates and "swipes" that save time.</option>
          <option value="results">Results: I have amazing client stories and data to share.</option>
          <option value="teaching">Teaching: I'm best at explaining the "how" and "why."</option>
        </select>
      </div>

      <div className="question-group">
        <label>8. Where will you primarily promote this lead magnet?</label>
        <select name="promotionChannel" value={formData.promotionChannel} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="social-bios">Social Media Bios (Instagram, TikTok)</option>
          <option value="blog-articles">Inside Blog Posts / Articles</option>
          <option value="paid-ads">Paid Advertising (Facebook, Google)</option>
          <option value="direct-outreach">Direct Outreach (DMs, 1:1 Emails)</option>
        </select>
      </div>

      <div className="question-group">
        <label>9. What "Vibe" best describes your brand?</label>
        <select name="brandVibe" value={formData.brandVibe} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="implementation-partner">The Implementation Partner: Practical, fast, and hands-on.</option>
          <option value="authority">The Authority: High-level, data-driven, and expert.</option>
          <option value="minimalist">The Minimalist: Short, sweet, and to the point.</option>
          <option value="visionary">The Visionary: Big-picture, inspiring, and transformative.</option>
        </select>
      </div>

      <div className="question-group">
        <label>10. What do you already have the most "raw material" for?</label>
        <select name="rawMaterial" value={formData.rawMaterial} onChange={handleChange} required>
          <option value="">Select one...</option>
          <option value="tips-hacks">I have lots of quick tips and "hacks" in my head.</option>
          <option value="specific-process">I have a very specific process I use for every client.</option>
          <option value="case-studies">I have past successful case studies and "before and afters."</option>
          <option value="templates">I have templates, spreadsheets, or scripts I use daily.</option>
        </select>
      </div>

      <button
        type="submit"
        className="submit-btn"
        disabled={!isFormValid()}
      >
        Find My Perfect Lead Magnet →
      </button>
    </form>
  );
}

export default QuizForm;
